import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { ShoppingBag, Menu, X } from "lucide-react";
import { Logo } from "./Logo";
import { useCart } from "../context/CartContext";

const links = [
  { label: "The Book", to: "/#book" },
  { label: "Read", to: "/read" },
  { label: "Dictionary", to: "/dictionary" },
  { label: "Framework", to: "/#framework" },
  { label: "Academy", to: "/academy" },
  { label: "Circles", to: "/circles" },
  { label: "Store", to: "/store" },
  { label: "Contact", to: "/#contact" },
];

export const Nav = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const { count, setIsOpen } = useCart();
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleNav = (to) => {
    setMobileOpen(false);
    if (to.startsWith("/#")) {
      const id = to.slice(2);
      if (location.pathname !== "/") {
        navigate("/");
        setTimeout(() => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" }), 300);
      } else {
        document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
      }
    } else {
      navigate(to);
    }
  };

  const dark = scrolled;

  return (
    <>
      <header
        data-testid="main-nav"
        className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-500 ${
          dark ? "bg-cream/90 backdrop-blur-md border-b border-line" : "bg-transparent"
        }`}
      >
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 h-20 flex items-center justify-between">
          <button
            data-testid="nav-logo"
            onClick={() => handleNav("/")}
            className="flex items-center gap-3 group"
          >
            <Logo className="w-8 h-8 text-emerald transition-transform duration-500 group-hover:rotate-[8deg]" stroke={dark ? "#0F3821" : "#D4AF37"} />
            <span className={`font-display text-lg tracking-tight leading-none font-bold ${dark ? "text-ink" : "text-cream"}`}>
              The Love Genome<span className="text-gold align-super text-[0.6em]">™</span>
            </span>
          </button>

          <nav className="hidden lg:flex items-center gap-9">
            {links.map((l) => (
              <button
                key={l.label}
                data-testid={`nav-link-${l.label.toLowerCase().replace(/\s/g, "-")}`}
                onClick={() => handleNav(l.to)}
                className={`font-body text-sm font-semibold link-underline transition-colors ${
                  dark ? "text-ink/85 hover:text-emerald" : "text-cream/90 hover:text-gold"
                }`}
              >
                {l.label}
              </button>
            ))}
          </nav>

          <div className="flex items-center gap-4">
            <button
              data-testid="open-cart-btn"
              onClick={() => setIsOpen(true)}
              className={`relative p-2 transition-colors ${dark ? "text-ink hover:text-emerald" : "text-cream hover:text-gold"}`}
              aria-label="Open cart"
            >
              <ShoppingBag className="w-5 h-5" strokeWidth={1.5} />
              {count > 0 && (
                <span className="absolute -top-0.5 -right-0.5 bg-gold text-ink text-[10px] font-semibold w-4 h-4 flex items-center justify-center rounded-full">
                  {count}
                </span>
              )}
            </button>
            <button
              className={`lg:hidden p-2 transition-colors ${dark ? "text-ink" : "text-cream"}`}
              onClick={() => setMobileOpen(true)}
              aria-label="Menu"
              data-testid="mobile-menu-btn"
            >
              <Menu className="w-6 h-6" strokeWidth={1.5} />
            </button>
          </div>
        </div>
      </header>

      {mobileOpen && (
        <div className="fixed inset-0 z-[70] bg-emerald text-cream flex flex-col" data-testid="mobile-menu">
          <div className="h-20 px-6 flex items-center justify-between border-b border-white/10">
            <span className="font-display text-lg">Menu</span>
            <button onClick={() => setMobileOpen(false)} aria-label="Close menu" data-testid="mobile-menu-close">
              <X className="w-6 h-6" />
            </button>
          </div>
          <nav className="flex-1 flex flex-col justify-center gap-2 px-8">
            {links.map((l) => (
              <button
                key={l.label}
                onClick={() => handleNav(l.to)}
                className="font-display text-4xl py-2 text-left hover:text-gold transition-colors"
              >
                {l.label}
              </button>
            ))}
          </nav>
        </div>
      )}
    </>
  );
};

export default Nav;
